import React, { useState, useCallback, useEffect } from 'react';
import { useSessionContext } from 'kmstack-shared/hooks';
import { getEnvConfig, getBillingNotice } from 'kmstack-shared/utils';

import Sidebar from '@/components/Sidebar';
import DashboardTopBar from '@/components/DashboardTopBar';
import { DashboardView, Page, DashboardViewTabs } from '@/types';

// Import views using relative paths
import { kmstackWithErrorBoundary } from '@/context/WithErrorBoundary';
import { getInitialViewFromUrl } from '@/common/utils/tabs';
import { KMStackEvent } from '@/common/utils';
import { useEventBusHandlers } from '@/common/hooks/useEventBusHandlers';

import { useUI } from '@/context/UIContext';
import { ViewContainer } from '@/common/detail-view/ViewContainer';
import { cn } from '@/common/ui/Core';
import { ConfirmationModal } from '@/common/ui/modals/ConfirmationModal';
import { SlidingCurtain } from '@/common/ui/SlidingCurtain';
import { AppVersion } from 'kmstack-shared/components';

interface DashboardPageProps {
  onNavigate: (page: Page, email?: string) => void;
  children?: React.ReactNode;
}



export const DashboardViewTabLabels: Record<DashboardViewTabs, string> = {
  [DashboardViewTabs.OVERVIEW]: 'Overview',
  [DashboardViewTabs.INVOICES]: 'Invoices',
  [DashboardViewTabs.CLIENTS_SUPPLIERS]: 'Clients & Suppliers',
  [DashboardViewTabs.WORKFORCE]: 'Workforce',
  [DashboardViewTabs.PAYMENTS]: 'Payments',
  [DashboardViewTabs.PURCHASES]: 'Purchases',
  [DashboardViewTabs.INVENTORY]: 'Inventory',
  [DashboardViewTabs.REPORT_ANALYTICS]: 'Reports & Analytics',
  [DashboardViewTabs.AUTOMATION]: 'Automation',
  [DashboardViewTabs.SECURITY_COMPLIANCE]: 'Security & Compliance',
  [DashboardViewTabs.INTEGRATIONS]: 'Integrations',
  [DashboardViewTabs.SETTINGS]: 'Settings',
  [DashboardViewTabs.EMPLOYEE_PORTAL]: ''
}

export const DashboardPage: React.FC<DashboardPageProps> = ({ children }) => {
  const { sessionContext } = useSessionContext();
  const billingNotice = getBillingNotice(sessionContext?.activeMembership);
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => window.innerWidth >= 1024);
  const [disableMainViewOnSave, setDisableMainViewOnSave] = useState<{
    value: boolean;
    msg?: string;
  } | null>(null);
  const [currentDashboardView, setCurrentDashboardView] = useState<DashboardView>(() => getInitialViewFromUrl(window.location.pathname));

  const {
    detailView, closeDetailView,
    globalIsDirty, setGlobalDirty,
    showUnsavedModal, setShowUnsavedModal,
    onConfirmClose,
    curtain, closeCurtain
  } = useUI();
  const isDetailViewOpen = !!detailView?.isOpen;

  useEventBusHandlers({
    [KMStackEvent.DISABLE_ENABLE_MAIN_VIEW]: (event: { value: boolean, msg?: string }) => {
      setDisableMainViewOnSave(event);
    }
  });


  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  let pageTitle = 'Dashboard';
  // the above switch is unneccessary, we can use the DashboardViewTabLabels directly
  pageTitle = DashboardViewTabLabels[currentDashboardView];

  const handleDetailBack = () => {
    if (detailView?.onBack) {
      detailView.onBack();
    } else {
      closeDetailView();
    }
  };

  // Navigation Interception Logic
  type PendingAction = { type: 'navigate'; view: DashboardView } | { type: 'close' };
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);

  const handleNavigationAttempt = useCallback((view: DashboardView) => {
    // Debug log
    console.log('handleNavigationAttempt', { view, isDetailViewOpen, globalIsDirty });
    if (isDetailViewOpen && globalIsDirty) {
      setPendingAction({ type: 'navigate', view });
      setShowUnsavedModal(true);
    } else {
      setCurrentDashboardView(view);
      if (window.innerWidth < 1024) setIsSidebarOpen(false);
      if (isDetailViewOpen) closeDetailView();
    }
  }, [isDetailViewOpen, globalIsDirty, closeDetailView]);

  const handleBackAttempt = useCallback(() => {
    // Debug log
    console.log('handleBackAttempt', { isDetailViewOpen, globalIsDirty });
    if (globalIsDirty) {
      setPendingAction({ type: 'close' });
      setShowUnsavedModal(true);
    } else {
      handleDetailBack();
    }
  }, [globalIsDirty, handleDetailBack]);

  const confirmAction = useCallback(() => {
    console.log('confirmAction', pendingAction);

    if (pendingAction?.type === 'navigate') {
      setCurrentDashboardView(pendingAction.view);
      if (isDetailViewOpen) closeDetailView();
    } else if (pendingAction?.type === 'close') {
      handleDetailBack();
    } else {
      // probably from form submit
      onConfirmClose(true);
    }
    // Clear dirty state since user is discarding changes
    setGlobalDirty(false);
    setPendingAction(null);
    // Use setTimeout to allow the click event to finish propagating/bubbling
    // before the modal unmounts, preventing "ghost clicks" on underlying elements.
    setTimeout(() => {
      setShowUnsavedModal(false);
    }, 0);
  }, [pendingAction, isDetailViewOpen, closeDetailView, handleDetailBack, setGlobalDirty]);

  const cancelAction = useCallback(() => {
    console.log('cancelAction - Closing Modal');
    setPendingAction(null);
    setTimeout(() => {
      setShowUnsavedModal(false);
    }, 0);
  }, []);

  // Sync state for modal (helper to keep rendering consistent if I missed a spot)
  useEffect(() => {
    // Optional debugging
    if (showUnsavedModal) console.log("Modal Open State: TRUE");
  }, [showUnsavedModal]);


  // if detail view is open, sidebar should be closed
  const isSidebarViewOpen = isDetailViewOpen ? false : isSidebarOpen;

  return (
    <div className="flex h-screen bg-gray-100">
      {isSidebarViewOpen && (
        <button
          type="button"
          aria-label="Close sidebar"
          className="fixed inset-0 z-[35] bg-slate-950/40 lg:hidden"
          onClick={toggleSidebar}
        />
      )}
      <Sidebar
        isOpen={isSidebarViewOpen}
        toggleSidebar={toggleSidebar}
        currentDashboardView={currentDashboardView}
        setCurrentDashboardView={handleNavigationAttempt}
        disabled={isDetailViewOpen && !!detailView?.onEdit}
      />

      <div className={cn(
        "flex-1 flex flex-col overflow-hidden transition-all duration-300 ease-in-out",
        isSidebarViewOpen ? 'lg:ml-64' : 'lg:ml-20'
      )}>
        <DashboardTopBar
          pageTitle={pageTitle}
          viewDescription={detailView?.viewDescription}
          onSidebarToggle={toggleSidebar}
          isSidebarOpen={isSidebarViewOpen}
          isDetailViewOpen={isDetailViewOpen}
          onBack={isDetailViewOpen ? handleBackAttempt : undefined}
          detailTitle={isDetailViewOpen ? detailView?.title : undefined}
        />
        {disableMainViewOnSave?.value && (
          <div className="absolute inset-0 bg-black bg-opacity-75 flex items-center justify-center z-10">
            <div className=" items-center ">
              <svg className="animate-spin h-8 w-8 text-primaryGreen" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span className="text-white font-medium">{disableMainViewOnSave?.msg || 'Saving...'}</span>
            </div>
          </div>
        )}
        <main className={`flex-1 overflow-x-hidden overflow-y-auto bg-gray-100 ${ isDetailViewOpen ? 'p-0' : 'p-6 md:p-8' }`}>
          {billingNotice && (
            <div role="status" className="flex flex-wrap items-center justify-between gap-3 border-b border-amber-200 bg-amber-50 p-3 text-sm mb-4">
              <p>{billingNotice} You can continue using Free features.</p>
              <a className="font-semibold underline shrink-0" href={`${getEnvConfig().accountUrl.replace(/\/$/, '')}/account/billing`}>Resolve billing</a>
            </div>
          )}
          <div className="container mx-auto dashboard-children" style={{ marginLeft: 0, marginRight: 0, maxWidth: '100%' }}>
            {/* Always keep children mounted to preserve state, but hide them when DetailView is open */}
            <div className={isDetailViewOpen ? 'hidden' : 'block'}>
              {children}
            </div>

            {isDetailViewOpen && (
              <ViewContainer
                onBack={handleBackAttempt}
                title={detailView.title}
                viewDescription={detailView.viewDescription}
                onEdit={detailView.onEdit}
                actions={detailView.actions}
                footerActions={detailView.footerActions}
              >
                {detailView.content}
              </ViewContainer>
            )}
          </div>
          <div className="absolute bottom-0 left-0 right-0 flex justify-center pb-4 text-gray-400 text-xs">
            <AppVersion />
          </div>
        </main>
      </div>

      <ConfirmationModal
        isOpen={showUnsavedModal}
        onClose={cancelAction}
        onConfirm={confirmAction}
        title="Unsaved Changes"
        message="You have unsaved changes. Are you sure you want to leave? Your changes will be lost."
        confirmText="Discard Changes"
        cancelText="Keep Editing"
        confirmVariant="danger"
      />

      {curtain && (
        <SlidingCurtain
          isOpen={curtain.isOpen}
          onClose={closeCurtain}
          title={curtain.title}
          subtitle={curtain.subtitle}
          icon={curtain.icon}
          description={curtain.description}
          actions={curtain.actions}
        >
          {curtain.children}
        </SlidingCurtain>
      )}
    </div>
  );
};

export const SafeDashboard = kmstackWithErrorBoundary(DashboardPage);
